import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  CircleAlert,
  FileWarning,
  HelpCircle,
  Info,
  Send,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FileUploader } from '@/components/import/file-uploader';
import { SalesImportPreview } from '@/components/import/sales-import-preview';
import { parseSalesCSV, type SalesCsvParseResult } from '@/lib/sales-csv-parser';
import { importSales } from '@/lib/api';
import type { SalesImportResponse, SalesImportRow } from '@/lib/api';

type Phase = 'upload' | 'preview' | 'executing' | 'done';

export function SalesImportFlow() {
  const [phase, setPhase] = useState<Phase>('upload');
  const [parseResult, setParseResult] = useState<SalesCsvParseResult | null>(null);
  const [validRows, setValidRows] = useState<SalesImportRow[]>([]);
  const [previewResponse, setPreviewResponse] = useState<SalesImportResponse | null>(null);
  const [executeResponse, setExecuteResponse] = useState<SalesImportResponse | null>(null);
  const [parsing, setParsing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelected = useCallback(async (file: File) => {
    setError(null);
    setPreviewResponse(null);
    setExecuteResponse(null);
    setParsing(true);
    try {
      const result = await parseSalesCSV(file);
      setParseResult(result);
      setValidRows(result.rows);

      if (result.errors.length > 0 || result.rows.length === 0) return;

      setPreviewing(true);
      const dryRun = await importSales(result.rows, true);
      setPreviewResponse(dryRun);
      setPhase('preview');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setParsing(false);
      setPreviewing(false);
    }
  }, []);

  const handleConfirmImport = useCallback(async () => {
    if (!validRows.length) return;
    setPhase('executing');
    setError(null);
    try {
      const exec = await importSales(validRows, false);
      setExecuteResponse(exec);
      setPhase('done');
    } catch (err) {
      setError((err as Error).message);
      setPhase('preview');
    }
  }, [validRows]);

  const handleReset = useCallback(() => {
    setPhase('upload');
    setParseResult(null);
    setValidRows([]);
    setPreviewResponse(null);
    setExecuteResponse(null);
    setError(null);
  }, []);

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Lỗi</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {phase === 'upload' && (
        <>
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertTitle>Định dạng CSV — Sales theo tháng</AlertTitle>
            <AlertDescription className="space-y-1.5">
              <p>
                Cột bắt buộc: <code className="rounded bg-muted px-1 py-0.5 text-xs">sku</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">tháng</code>.
              </p>
              <p>
                Cột tự nhận diện:{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">sản lượng</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">doanh thu</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">lợi nhuận</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">tồn kho</code>.
              </p>
              <p>
                Format tháng chấp nhận: <code className="rounded bg-muted px-1 py-0.5 text-xs">2026-03</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">T3/2026</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">03/2026</code>,{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">Tháng 3 2026</code>.
              </p>
              <p>
                SKU phải tồn tại trong DB trước khi import sales. Cùng (SKU, tháng) sẽ được{' '}
                <span className="font-semibold">update</span> nếu đã có.
              </p>
            </AlertDescription>
          </Alert>

          <FileUploader onFileSelected={handleFileSelected} disabled={parsing || previewing} />

          {(parsing || previewing) && (
            <div className="rounded-lg border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
              {parsing ? 'Đang parse CSV...' : 'Đang kiểm tra với database...'}
            </div>
          )}

          {parseResult && parseResult.errors.length > 0 && (
            <Alert variant="destructive">
              <CircleAlert className="h-4 w-4" />
              <AlertTitle>Không thể parse file</AlertTitle>
              <AlertDescription>
                <ul className="ml-4 list-disc space-y-0.5">
                  {parseResult.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </>
      )}

      {phase === 'preview' && previewResponse && parseResult && (
        <>
          {parseResult.warnings.length > 0 && (
            <Alert variant="warning">
              <FileWarning className="h-4 w-4" />
              <AlertTitle>{parseResult.warnings.length} cảnh báo từ parser</AlertTitle>
              <AlertDescription>
                <ul className="ml-4 list-disc space-y-0.5 text-xs">
                  {parseResult.warnings.slice(0, 5).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                  {parseResult.warnings.length > 5 && (
                    <li className="text-muted-foreground">... và {parseResult.warnings.length - 5} khác</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {parseResult.unmappedHeaders.length > 0 && (
            <Alert variant="info">
              <HelpCircle className="h-4 w-4" />
              <AlertTitle>Cột không nhận diện được — sẽ bỏ qua</AlertTitle>
              <AlertDescription className="text-xs">
                {parseResult.unmappedHeaders.join(', ')}
              </AlertDescription>
            </Alert>
          )}

          <SalesImportPreview
            rows={validRows}
            results={previewResponse.rows}
            summary={previewResponse.summary}
          />

          <div className="flex items-center justify-between rounded-lg border bg-card p-3">
            <div className="text-sm text-muted-foreground">
              Sẵn sàng áp dụng{' '}
              <span className="font-semibold text-foreground">
                {previewResponse.summary.new + previewResponse.summary.update}
              </span>{' '}
              dòng sales vào database.
              {previewResponse.summary.errors > 0 && (
                <span className="ml-1 text-destructive">
                  · {previewResponse.summary.errors} dòng lỗi sẽ bị bỏ qua.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleReset}>
                Hủy
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmImport}
                disabled={previewResponse.summary.new + previewResponse.summary.update === 0}
              >
                <Send className="h-4 w-4" />
                Áp dụng import
              </Button>
            </div>
          </div>
        </>
      )}

      {phase === 'executing' && (
        <div className="rounded-lg border bg-card p-12 text-center">
          <Upload className="mx-auto h-8 w-8 animate-pulse text-primary" />
          <p className="mt-3 text-sm font-medium">Đang import sales vào database...</p>
        </div>
      )}

      {phase === 'done' && executeResponse && (
        <>
          <Alert variant="success">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Import sales thành công</AlertTitle>
            <AlertDescription>
              Đã insert <strong>{executeResponse.inserted ?? 0}</strong> dòng mới và update{' '}
              <strong>{executeResponse.updated ?? 0}</strong> dòng.{' '}
              {executeResponse.summary.errors > 0 && (
                <span className="text-destructive">
                  ({executeResponse.summary.errors} dòng bị bỏ qua do lỗi)
                </span>
              )}
            </AlertDescription>
          </Alert>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <Upload className="h-4 w-4" />
              Import file khác
            </Button>
            <Button asChild size="sm">
              <Link to="/">Xem Product Map</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
